import React, {FC, useRef, useState} from "react";
import {Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Typography} from "antd";
import dayjs, {Dayjs} from "dayjs";
import {useTranslation} from "react-i18next";
import {PaymentMethod, useRecordOfferPaymentMutation} from "../../GraphQL/generated/graphql";

import {formatMoney} from "../../format";
import {normalizeEnumKey} from "../../i18n/labelHelpers";

export type PaymentEntryOffer = {
    id: number; offerNumber?: string | null; participantFullName?: string | null;
    eventName?: string | null; status?: string | null; grossTotal?: number | null; currency?: string | null;
};
type Values = {offerId: number; amount: number; paidAt: Dayjs; method: PaymentMethod; reference?: string; note: string};
const RecordPaymentDialog: FC<{payerId: number; offers: PaymentEntryOffer[]; initialOfferId?: number; onClose: () => void; onSaved: () => Promise<unknown>}> =
({payerId, offers, initialOfferId, onClose, onSaved}) => {
    const {t} = useTranslation();
    const [form] = Form.useForm<Values>();
    const [record, state] = useRecordOfferPaymentMutation();
    const saving = useRef(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);
    const eligible = offers.filter(offer => !["EXPIRED", "REPLACED"].includes(normalizeEnumKey(offer.status)) && Number(offer.grossTotal) > 0);
    const offerId = Form.useWatch("offerId", form);
    const selected = eligible.find(offer => offer.id === offerId);
    const submit = async (values: Values) => {
        if (saving.current || saved) return;
        saving.current = true;
        setError("");
        try {
            const result = await record({variables: {input: {
                payerId, offerId: values.offerId, amount: values.amount, method: values.method,
                paidAt: values.paidAt.startOf("day").toISOString(), reference: values.reference?.trim() ?? "", note: values.note.trim()
            }}});
            if (!result.data?.recordOfferPayment.id) throw new Error(t("payment-entry-failed"));
            setSaved(true);
            try {await onSaved(); onClose();}
            catch {setError(t("payment-entry-refresh-failed"));}
        } catch (e) {
            setError(e instanceof Error ? e.message : t("payment-entry-failed"));
        } finally {saving.current = false;}
    };
    return <Modal open title={t("payment-entry-title")} footer={null} onCancel={() => {if (!saving.current) onClose();}}
                  closable={!state.loading} maskClosable={false}>
        <div className="ati-stack">
            <Alert type="info" message={t("payment-entry-help")}/>
            {error && <Alert type={saved ? "warning" : "error"} message={error} showIcon/>}
            {!eligible.length && <Alert type="warning" message={t("payment-entry-no-offers")}/>}
            <Form form={form} layout="vertical" onFinish={submit} disabled={state.loading || saved}
                  initialValues={{offerId: eligible.some(offer => offer.id === initialOfferId) ? initialOfferId : undefined,
                      paidAt: dayjs(), method: PaymentMethod.BankTransfer}}>
                <Form.Item name="offerId" label={t("payment-entry-offer")} rules={[{required: true, message: t("payment-entry-required")}]}>
                    <Select showSearch optionFilterProp="label" options={eligible.map(offer => ({value: offer.id,
                        label: `${offer.offerNumber || offer.id} · ${offer.participantFullName || ""} · ${offer.eventName || ""}`}))}/>
                </Form.Item>
                {selected && <Typography.Paragraph>{t("payment-entry-offer-total")}: {formatMoney(selected.grossTotal, selected.currency)}</Typography.Paragraph>}
                <Form.Item name="amount" label={`${t("payment-entry-amount")}${selected?.currency ? ` (${selected.currency})` : ""}`}
                           rules={[{required: true, type: "number", min: 0.01, message: t("payment-entry-positive")}]}>
                    <InputNumber min={0.01} precision={2} style={{width: "100%"}}/>
                </Form.Item>
                <Form.Item name="paidAt" label={t("payment-entry-date")} rules={[{required: true, message: t("payment-entry-required")}]}>
                    <DatePicker format="DD.MM.YYYY" disabledDate={date => date.isAfter(dayjs(), "day")} style={{width: "100%"}}/>
                </Form.Item>
                <Form.Item name="method" label={t("payment-entry-method")} rules={[{required: true}]}>
                    <Select options={[
                        {value: PaymentMethod.BankTransfer, label: t("payment-entry-bank")},
                        {value: PaymentMethod.Pos, label: "POS"},
                        {value: PaymentMethod.Cash, label: t("payment-entry-cash")},
                        {value: PaymentMethod.Sponsor, label: t("payment-entry-sponsor")}
                    ]}/>
                </Form.Item>
                <Form.Item name="reference" label={t("payment-entry-reference")}><Input maxLength={256}/></Form.Item>
                <Form.Item name="note" label={t("payment-entry-note")} rules={[{required: true, whitespace: true, message: t("payment-entry-required")}]}>
                    <Input.TextArea rows={3} maxLength={1024}/>
                </Form.Item>
                <Space>
                    <Button type="primary" htmlType="submit" loading={state.loading} disabled={!eligible.length || saved}>{t("payment-entry-save")}</Button>
                    <Button onClick={onClose} disabled={state.loading}>{t("payment-entry-close")}</Button>
                </Space>
            </Form>
        </div>
    </Modal>;
};
export default RecordPaymentDialog;
